import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Card, Input, Select, DatePicker, Button, Tag, Avatar, App as AntdApp,
  Modal, Pagination, Spin, Segmented, Empty, Form, DatePicker as AntDatePicker,
  InputNumber, Radio, Tabs, Switch, Row, Col, Checkbox, Space, Collapse, Rate,
} from 'antd';
import {
  Download, Search, MessageSquare, Heart, Lightbulb, AlertTriangle,
  User as UserIcon, CheckCircle2, Clock, Archive as ArchiveIcon,
  ListChecks, LayoutGrid, LayoutList, Plus, ClipboardList, ExternalLink,
  Eye, FileText, Image as ImageIcon, File as FileIcon, X as XIcon,
} from 'lucide-react';
import dayjs from 'dayjs';
import { PageHeader } from '@/components/common/PageHeader';
import { PdfFullViewer } from '@/components/signing/PdfFullViewer';
import {
  adminFeedbackService, adminFeedbackV2,
  type Feedback, type Sentiment, type FeedbackStatus,
  type FeedbackKanbanColumn, type FeedbackLinkedTask,
  SENTIMENT_LABELS, SENTIMENT_COLORS,
  STATUS_LABELS, STATUS_COLORS,
} from '@/services/feedbackService';
import { orgService } from '@/services/rolesService';
import { userService } from '@/services/userService';

/**
 * V1.0.37 + V1.0.38: Trang Quản trị Hộp thư lắng nghe.
 *  - Tab "Đang xử lý" (List + Kanban toggle) — ẩn ARCHIVED
 *  - Tab "Lưu trữ" — chỉ feedback ARCHIVED
 *  - Detail modal: archive / create task / view linked tasks
 */
export function FeedbackInboxPage() {
  const { message, modal } = AntdApp.useApp();
  const qc = useQueryClient();
  const navigate = useNavigate();

  // Tab + view mode
  const [tab, setTab] = useState<'active' | 'archived'>('active');
  const [viewMode, setViewMode] = useState<'list' | 'kanban'>(() => {
    return (localStorage.getItem('hv-feedback-view') as 'list' | 'kanban') ?? 'list';
  });

  // Filter state
  const [search, setSearch] = useState('');
  const [topicId, setTopicId] = useState<string | undefined>();
  const [sentiment, setSentiment] = useState<Sentiment | undefined>();
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);

  // Selected for detail modal
  const [selected, setSelected] = useState<Feedback | null>(null);
  // Create task modal
  const [createTaskOpen, setCreateTaskOpen] = useState(false);

  const setViewModeWithCache = (m: 'list' | 'kanban') => {
    setViewMode(m);
    localStorage.setItem('hv-feedback-view', m);
  };

  // Queries
  const { data: stats } = useQuery({
    queryKey: ['admin-feedback-stats'],
    queryFn: () => adminFeedbackService.stats(),
    refetchInterval: 60_000,
  });

  const { data: topics = [] } = useQuery({
    queryKey: ['admin-feedback-topics'],
    queryFn: () => adminFeedbackService.listTopics(true),
  });

  // Tab "active" → loại bỏ ARCHIVED, tab "archived" → chỉ ARCHIVED
  const tabStatus: FeedbackStatus | undefined = tab === 'archived' ? 'ARCHIVED' : undefined;

  const queryParams = {
    search: search.trim() || undefined,
    topicId,
    sentiment,
    status: tabStatus,
    fromDate: dateRange?.[0]?.format('YYYY-MM-DD'),
    toDate: dateRange?.[1]?.format('YYYY-MM-DD'),
    page,
    pageSize,
  };

  const { data: inbox, isLoading: inboxLoading } = useQuery({
    queryKey: ['admin-feedback-inbox', queryParams],
    queryFn: () => adminFeedbackService.listInbox(queryParams),
    enabled: tab === 'archived' || viewMode === 'list',
    refetchInterval: 60_000,
  });

  // Kanban view — chỉ load khi đang ở tab active + kanban mode
  const { data: kanban, isLoading: kanbanLoading } = useQuery({
    queryKey: ['admin-feedback-kanban', { search, sentiment, dateRange: dateRange?.map(d => d?.format('YYYY-MM-DD')) }],
    queryFn: () => adminFeedbackV2.kanban({
      search: search.trim() || undefined,
      sentiment,
      fromDate: dateRange?.[0]?.format('YYYY-MM-DD'),
      toDate: dateRange?.[1]?.format('YYYY-MM-DD'),
    }),
    enabled: tab === 'active' && viewMode === 'kanban',
    refetchInterval: 60_000,
  });

  // Mutations
  const updateStatusMut = useMutation({
    mutationFn: ({ id, status, note }: { id: string; status: FeedbackStatus; note?: string }) =>
      adminFeedbackService.updateStatus(id, status, note),
    onSuccess: () => {
      message.success('Đã cập nhật trạng thái');
      qc.invalidateQueries({ queryKey: ['admin-feedback-inbox'] });
      qc.invalidateQueries({ queryKey: ['admin-feedback-kanban'] });
      qc.invalidateQueries({ queryKey: ['admin-feedback-stats'] });
      setSelected(null);
    },
  });

  const archiveMut = useMutation({
    mutationFn: (id: string) => adminFeedbackV2.archive(id),
    onSuccess: () => {
      message.success('Đã đẩy phản hồi sang lưu trữ');
      qc.invalidateQueries({ queryKey: ['admin-feedback-inbox'] });
      qc.invalidateQueries({ queryKey: ['admin-feedback-kanban'] });
      qc.invalidateQueries({ queryKey: ['admin-feedback-stats'] });
      setSelected(null);
    },
    onError: (e: any) => message.error(e?.response?.data?.message ?? 'Lưu trữ thất bại'),
  });

  const handleExport = async () => {
    try {
      const blob = await adminFeedbackService.exportExcel(queryParams);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `HopThuLangNghe_${dayjs().format('YYYYMMDD_HHmm')}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      message.success('Đã xuất Excel');
    } catch (e: any) {
      message.error(e?.response?.data?.message ?? 'Xuất Excel thất bại');
    }
  };

  return (
    <>
      <PageHeader
        breadcrumb="QUẢN TRỊ"
        title="Quản trị Hộp thư lắng nghe"
        subtitle="Theo dõi và quản lý những tâm tình từ bệnh nhân thời gian thực."
        extra={
          <Button type="primary" icon={<Download size={14} />} onClick={handleExport}>
            Xuất Excel
          </Button>
        }
      />

      {/* Stats cards — responsive 4→2→1 columns */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={12} md={6}>
          <StatCard label="Tổng thông điệp" value={stats?.totalMessages ?? 0} color="#1e6feb" icon={<MessageSquare size={32} style={{ opacity: 0.15 }} />} />
        </Col>
        <Col xs={12} md={6}>
          <StatCard label="Tâm tình tích cực" value={stats?.positiveCount ?? 0} color="#52c41a" icon={<Heart size={32} style={{ opacity: 0.15 }} />} extra={stats ? `${stats.positivePercent}%` : undefined} />
        </Col>
        <Col xs={12} md={6}>
          <StatCard label="Góp ý xây dựng" value={stats?.suggestionCount ?? 0} color="#faad14" icon={<MessageSquare size={32} style={{ opacity: 0.15 }} />} />
        </Col>
        <Col xs={12} md={6}>
          <StatCard label="Cần lưu tâm" value={stats?.complaintCount ?? 0} color="#f5222d" icon={<AlertTriangle size={32} style={{ opacity: 0.15 }} />} extra={stats ? `${stats.complaintPercent}%` : undefined} />
        </Col>
      </Row>

      {/* Tabs: Đang xử lý / Lưu trữ */}
      <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <Tabs
            activeKey={tab}
            onChange={(k) => { setTab(k as any); setPage(1); }}
            items={[
              { key: 'active', label: <span><ListChecks size={13} style={{ verticalAlign: -2, marginRight: 4 }} /> Đang xử lý</span> },
              { key: 'archived', label: <span><ArchiveIcon size={13} style={{ verticalAlign: -2, marginRight: 4 }} /> Lưu trữ</span> },
            ]}
            style={{ marginBottom: -4 }}
          />
          {tab === 'active' && (
            <Segmented
              size="small"
              value={viewMode}
              onChange={(v) => setViewModeWithCache(v as any)}
              options={[
                { label: <span><LayoutList size={12} style={{ verticalAlign: -2, marginRight: 4 }} />List</span>, value: 'list' },
                { label: <span><LayoutGrid size={12} style={{ verticalAlign: -2, marginRight: 4 }} />Kanban</span>, value: 'kanban' },
              ]}
            />
          )}
        </div>
      </Card>

      {/* Filter bar — responsive */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[10, 10]}>
          <Col xs={24} md={viewMode === 'kanban' ? 12 : 9}>
            <Input
              placeholder="Tìm kiếm nội dung..."
              prefix={<Search size={14} style={{ color: '#7d8a98' }} />}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              allowClear
            />
          </Col>
          {viewMode === 'list' && (
            <Col xs={24} sm={12} md={5}>
              <Select
                style={{ width: '100%' }}
                placeholder="Tất cả chủ đề"
                value={topicId}
                onChange={(v) => { setTopicId(v); setPage(1); }}
                allowClear
                options={topics.map(t => ({ value: t.id, label: `${t.emoji ?? '💬'} ${t.name}` }))}
              />
            </Col>
          )}
          <Col xs={24} sm={12} md={viewMode === 'kanban' ? 5 : 4}>
            <Select
              style={{ width: '100%' }}
              placeholder="Phân loại"
              value={sentiment}
              onChange={(v) => { setSentiment(v); setPage(1); }}
              allowClear
              options={(['POSITIVE', 'SUGGESTION', 'IDEA', 'COMPLAINT'] as Sentiment[]).map(s => ({
                value: s, label: SENTIMENT_LABELS[s],
              }))}
            />
          </Col>
          <Col xs={24} md={viewMode === 'kanban' ? 7 : 6}>
            <DatePicker.RangePicker
              value={dateRange}
              onChange={(v) => { setDateRange(v); setPage(1); }}
              format="DD/MM/YYYY"
              placeholder={['Từ ngày', 'Đến ngày']}
              style={{ width: '100%' }}
            />
          </Col>
        </Row>
      </Card>

      {/* === KANBAN VIEW === */}
      {viewMode === 'kanban' && tab === 'active' && (
        <>
          {kanbanLoading ? (
            <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>
          ) : !kanban?.length ? (
            <Card><Empty description="Chưa có chủ đề nào hoặc phản hồi nào" /></Card>
          ) : (
            <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 8 }}>
              {kanban.map((col) => (
                <KanbanColumn
                  key={col.topicId ?? 'none'}
                  column={col}
                  onItemClick={setSelected}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* === LIST VIEW === */}
      {(viewMode === 'list' || tab === 'archived') && (
        <>
          {inboxLoading ? (
            <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>
          ) : !inbox?.items.length ? (
            <Card><div style={{ textAlign: 'center', padding: 40, color: '#7d8a98' }}>
              {tab === 'archived'
                ? 'Chưa có phản hồi nào trong lưu trữ.'
                : 'Chưa có thông điệp nào phù hợp với bộ lọc.'}
            </div></Card>
          ) : (
            <>
              {inbox.items.map((f) => (
                <FeedbackCard key={f.id} feedback={f} onClick={() => setSelected(f)} />
              ))}

              <div style={{ marginTop: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: '#7d8a98' }}>
                  Hiển thị {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, inbox.total)} trên {inbox.total} thông điệp
                </span>
                <Pagination
                  current={page}
                  total={inbox.total}
                  pageSize={pageSize}
                  onChange={setPage}
                  showSizeChanger={false}
                />
              </div>
            </>
          )}
        </>
      )}

      {/* Detail modal */}
      {selected && (
        <FeedbackDetailModal
          feedback={selected}
          onClose={() => setSelected(null)}
          onResolve={(note) => updateStatusMut.mutate({ id: selected.id, status: 'RESOLVED', note })}
          onProcess={() => updateStatusMut.mutate({ id: selected.id, status: 'PROCESSING' })}
          onIgnore={() => {
            modal.confirm({
              title: 'Bỏ qua phản hồi này?',
              content: 'Phản hồi sẽ không được xử lý nữa.',
              onOk: () => updateStatusMut.mutate({ id: selected.id, status: 'IGNORED' }),
            });
          }}
          onArchive={() => {
            modal.confirm({
              title: 'Đẩy phản hồi sang lưu trữ?',
              content: 'Phản hồi sẽ ẩn khỏi tab "Đang xử lý" và chuyển sang tab "Lưu trữ".',
              onOk: () => archiveMut.mutate(selected.id),
            });
          }}
          onCreateTask={() => setCreateTaskOpen(true)}
          onNavigateToTask={(taskId) => navigate(`/tasks/${taskId}`)}
          updating={updateStatusMut.isPending || archiveMut.isPending}
        />
      )}

      {/* Create task modal */}
      {selected && createTaskOpen && (
        <CreateTaskFromFeedbackModal
          feedback={selected}
          topics={topics}
          onClose={() => setCreateTaskOpen(false)}
          onCreated={() => {
            setCreateTaskOpen(false);
            // Refresh linked tasks list trong detail modal
            qc.invalidateQueries({ queryKey: ['feedback-linked-tasks', selected.id] });
            qc.invalidateQueries({ queryKey: ['admin-feedback-inbox'] });
            qc.invalidateQueries({ queryKey: ['admin-feedback-kanban'] });
          }}
        />
      )}
    </>
  );
}

// ===================================
// Sub-components
// ===================================

function StatCard({ label, value, color, icon, extra }: {
  label: string; value: number; color: string; icon: React.ReactNode; extra?: string;
}) {
  return (
    <Card bodyStyle={{ padding: 20, position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 10, right: 10, color }}>{icon}</div>
      <div style={{ fontSize: 11, color: '#7d8a98', textTransform: 'uppercase', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 32, fontWeight: 700, color, marginTop: 6, fontFamily: 'serif' }}>
        {value.toLocaleString('vi-VN')}
      </div>
      {extra && <div style={{ fontSize: 11, color: '#7d8a98', marginTop: 4 }}>{extra}</div>}
    </Card>
  );
}

function KanbanColumn({ column, onItemClick }: {
  column: FeedbackKanbanColumn;
  onItemClick: (f: Feedback) => void;
}) {
  return (
    <div style={{
      flex: '0 0 320px', minWidth: 320,
      background: '#f0f4f8', borderRadius: 10, padding: 10,
      maxHeight: 'calc(100vh - 380px)', display: 'flex', flexDirection: 'column',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10,
        paddingBottom: 8, borderBottom: '2px solid #fff',
      }}>
        <span style={{ fontSize: 22 }}>{column.topicEmoji ?? '💬'}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {column.topicName}
          </div>
        </div>
        <Tag color="#1e6feb" style={{ marginInlineEnd: 0 }}>{column.totalCount}</Tag>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {column.items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 20, fontSize: 12, color: '#bdc4cd' }}>
            Chưa có phản hồi nào
          </div>
        ) : column.items.map(f => (
          <MiniFeedbackCard key={f.id} feedback={f} onClick={() => onItemClick(f)} />
        ))}
        {column.totalCount > column.items.length && (
          <div style={{ textAlign: 'center', fontSize: 11, color: '#7d8a98', padding: 8 }}>
            ...còn {column.totalCount - column.items.length} phản hồi khác
          </div>
        )}
      </div>
    </div>
  );
}

function MiniFeedbackCard({ feedback, onClick }: { feedback: Feedback; onClick: () => void }) {
  const color = SENTIMENT_COLORS[feedback.sentiment];
  return (
    <div
      onClick={onClick}
      style={{
        background: '#fff', borderRadius: 8, padding: 10,
        borderLeft: `3px solid ${color}`,
        cursor: 'pointer',
        transition: 'all 0.15s',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
      }}
      onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'}
      onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.04)'}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <Tag color={color} style={{ marginInlineEnd: 0, fontSize: 10, padding: '0 6px' }}>
          {SENTIMENT_LABELS[feedback.sentiment]}
        </Tag>
        <Tag color={STATUS_COLORS[feedback.status]} style={{ marginInlineEnd: 0, fontSize: 10, padding: '0 6px' }}>
          {STATUS_LABELS[feedback.status]}
        </Tag>
      </div>
      <div style={{ fontSize: 12, color: '#1e293b', lineHeight: 1.4, marginBottom: 4 }}>
        {feedback.content.length > 100 ? feedback.content.substring(0, 100) + '...' : feedback.content}
      </div>
      <div style={{ fontSize: 10, color: '#7d8a98', display: 'flex', justifyContent: 'space-between' }}>
        <span>{feedback.isAnonymous ? 'Ẩn danh' : (feedback.senderName ?? '—')}</span>
        <span>{dayjs(feedback.submittedAt).format('DD/MM HH:mm')}</span>
      </div>
    </div>
  );
}

function FeedbackCard({ feedback, onClick }: { feedback: Feedback; onClick: () => void }) {
  const color = SENTIMENT_COLORS[feedback.sentiment];
  return (
    <Card
      onClick={onClick}
      style={{
        marginBottom: 12, borderLeft: `4px solid ${color}`,
        cursor: 'pointer', transition: 'all 0.15s',
      }}
      bodyStyle={{ padding: 16 }}
      hoverable
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <Avatar size="small" icon={<UserIcon size={12} />} style={{ background: feedback.isAnonymous ? '#7d8a98' : '#1976D2' }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>
            {feedback.isAnonymous ? 'Ẩn danh' : (feedback.senderName ?? '—')}
          </div>
          <div style={{ fontSize: 11, color: '#7d8a98' }}>
            {dayjs(feedback.submittedAt).format('HH:mm, DD/MM/YYYY')}
          </div>
        </div>
        <Tag color={color} style={{ marginInlineEnd: 0 }}>
          {SENTIMENT_LABELS[feedback.sentiment]}
        </Tag>
      </div>

      <div style={{ fontFamily: 'serif', fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
        {feedback.topicEmoji} {feedback.topicName ?? 'Phản hồi tự do'}
      </div>

      <div style={{ fontSize: 13, color: '#5b6875', lineHeight: 1.6, marginBottom: 8 }}>
        {feedback.content.length > 200 ? feedback.content.substring(0, 200) + '...' : feedback.content}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {feedback.hashtags.map(t => (
            <span key={t} style={{ fontSize: 11, color: '#1e6feb', background: '#e8f1ff', padding: '2px 8px', borderRadius: 4 }}>#{t}</span>
          ))}
          {feedback.departmentName && (
            <span style={{ fontSize: 11, color: '#7d8a98', background: '#f0f4f8', padding: '2px 8px', borderRadius: 4 }}>
              📍 {feedback.departmentName}
            </span>
          )}
        </div>
        <Tag color={STATUS_COLORS[feedback.status]} style={{ marginInlineEnd: 0 }}>
          {STATUS_LABELS[feedback.status]}
        </Tag>
      </div>
    </Card>
  );
}

function FeedbackDetailModal({
  feedback, onClose, onResolve, onProcess, onIgnore, onArchive,
  onCreateTask, onNavigateToTask, updating,
}: {
  feedback: Feedback;
  onClose: () => void;
  onResolve: (note?: string) => void;
  onProcess: () => void;
  onIgnore: () => void;
  onArchive: () => void;
  onCreateTask: () => void;
  onNavigateToTask: (taskId: string) => void;
  updating: boolean;
}) {
  const [note, setNote] = useState('');
  const isArchived = feedback.status === 'ARCHIVED';

  // Load linked tasks
  const { data: linkedTasks = [], isLoading: tasksLoading } = useQuery({
    queryKey: ['feedback-linked-tasks', feedback.id],
    queryFn: () => adminFeedbackV2.listLinkedTasks(feedback.id),
  });

  return (
    <Modal
      open
      onCancel={onClose}
      width={780}
      title={
        <div>
          <div style={{ fontFamily: 'serif', fontSize: 18 }}>
            {feedback.topicEmoji} {feedback.topicName ?? 'Phản hồi tự do'}
          </div>
          <div style={{ fontSize: 11, color: '#7d8a98', marginTop: 4 }}>
            Mã: {feedback.code} · {dayjs(feedback.submittedAt).format('HH:mm DD/MM/YYYY')}
          </div>
        </div>
      }
      footer={null}
    >
      <div style={{ marginBottom: 16 }}>
        <Tag color={SENTIMENT_COLORS[feedback.sentiment]}>{SENTIMENT_LABELS[feedback.sentiment]}</Tag>
        <Tag color={STATUS_COLORS[feedback.status]}>{STATUS_LABELS[feedback.status]}</Tag>
        {feedback.priority === 'HIGH' && <Tag color="red">Ưu tiên cao</Tag>}
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: '#7d8a98', fontWeight: 600, marginBottom: 4 }}>NỘI DUNG</div>
        <div style={{ background: '#f8faff', padding: 12, borderRadius: 8, fontSize: 14, lineHeight: 1.6 }}>
          {feedback.content}
        </div>
      </div>

      {!feedback.isAnonymous && (
        <div style={{ marginBottom: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <div style={{ fontSize: 11, color: '#7d8a98', fontWeight: 600 }}>NGƯỜI GỬI</div>
            <div style={{ fontSize: 13 }}>{feedback.senderName ?? '—'}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: '#7d8a98', fontWeight: 600 }}>SĐT</div>
            <div style={{ fontSize: 13 }}>{feedback.senderPhone ?? '—'}</div>
          </div>
        </div>
      )}

      {feedback.departmentName && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: '#7d8a98', fontWeight: 600 }}>KHOA THĂM KHÁM</div>
          <div style={{ fontSize: 13 }}>{feedback.departmentName}</div>
        </div>
      )}

      {feedback.attachments.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: '#7d8a98', fontWeight: 600, marginBottom: 6 }}>FILE ĐÍNH KÈM</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {feedback.attachments.map(a => (
              <AttachmentItem key={a.id} attachment={a} />
            ))}
          </div>
        </div>
      )}

      {/* V1.0.38: Linked tasks */}
      <div style={{ marginBottom: 16, background: '#f8faff', padding: 12, borderRadius: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>
            <ClipboardList size={13} style={{ verticalAlign: -2, marginRight: 4 }} />
            Task đã tạo ({linkedTasks.length})
          </div>
          {!isArchived && (
            <Button size="small" type="primary" icon={<Plus size={12} />} onClick={onCreateTask}>
              Tạo task xử lý
            </Button>
          )}
        </div>
        {tasksLoading ? (
          <Spin size="small" />
        ) : linkedTasks.length === 0 ? (
          <div style={{ fontSize: 12, color: '#7d8a98', textAlign: 'center', padding: 12 }}>
            Chưa có task nào. Bấm "Tạo task xử lý" để giao việc cho khoa liên quan.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {linkedTasks.map((t) => (
              <LinkedTaskRow key={t.id} task={t} onClick={() => onNavigateToTask(t.taskId)} />
            ))}
          </div>
        )}
      </div>

      {feedback.status === 'RESOLVED' && feedback.resolutionNote && (
        <div style={{ marginBottom: 16, background: '#f6ffed', padding: 12, borderRadius: 8 }}>
          <div style={{ fontSize: 11, color: '#52c41a', fontWeight: 600 }}>GHI CHÚ XỬ LÝ</div>
          <div style={{ fontSize: 13 }}>{feedback.resolutionNote}</div>
          <div style={{ fontSize: 11, color: '#7d8a98', marginTop: 4 }}>
            Xử lý bởi {feedback.resolvedByName ?? '—'} · {feedback.resolvedAt && dayjs(feedback.resolvedAt).format('DD/MM/YYYY')}
          </div>
        </div>
      )}

      {!isArchived && feedback.status !== 'RESOLVED' && feedback.status !== 'IGNORED' && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: '#7d8a98', fontWeight: 600, marginBottom: 4 }}>
            GHI CHÚ XỬ LÝ (TÙY CHỌN)
          </div>
          <Input.TextArea
            rows={2}
            placeholder="Đã liên hệ bệnh nhân, đã chuyển bộ phận liên quan..."
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: 16 }}>
        {!isArchived && (
          <Button onClick={onArchive} loading={updating} icon={<ArchiveIcon size={14} />}>
            Lưu trữ
          </Button>
        )}
        {!isArchived && feedback.status !== 'RESOLVED' && feedback.status !== 'IGNORED' && (
          <>
            <Button onClick={onIgnore} loading={updating}>Bỏ qua</Button>
            {feedback.status === 'NEW' && (
              <Button onClick={onProcess} loading={updating} icon={<Clock size={14} />}>
                Đang xử lý
              </Button>
            )}
            <Button
              type="primary"
              loading={updating}
              icon={<CheckCircle2 size={14} />}
              onClick={() => onResolve(note.trim() || undefined)}
            >
              Đánh dấu đã xử lý
            </Button>
          </>
        )}
        {isArchived && (
          <span style={{ fontSize: 12, color: '#7d8a98', alignSelf: 'center' }}>
            Phản hồi đã ở trạng thái lưu trữ — không thể chỉnh sửa.
          </span>
        )}
      </div>
    </Modal>
  );
}

function LinkedTaskRow({ task, onClick }: { task: FeedbackLinkedTask; onClick: () => void }) {
  const statusColor: Record<string, string> = {
    NEW: '#1e6feb', ASSIGNED: '#1e6feb', IN_PROGRESS: '#faad14',
    COMPLETED: '#52c41a', CANCELLED: '#7d8a98', PENDING_REVIEW: '#fa8c16',
  };
  return (
    <div
      onClick={onClick}
      style={{
        background: '#fff', padding: 10, borderRadius: 6,
        border: '1px solid #e8edf2', cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 10,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {task.taskCode} · {task.taskTitle}
        </div>
        <div style={{ fontSize: 11, color: '#7d8a98', marginTop: 2 }}>
          {task.departmentName ?? '—'} · 👤 {task.assigneeName ?? 'Chưa giao'}
          {task.dueDate && ` · 📅 ${dayjs(task.dueDate).format('DD/MM')}`}
        </div>
      </div>
      <Tag color={statusColor[task.taskStatus] ?? '#7d8a98'} style={{ marginInlineEnd: 0, fontSize: 10 }}>
        {task.taskStatus}
      </Tag>
      <ExternalLink size={14} style={{ color: '#7d8a98' }} />
    </div>
  );
}


// ============================================================
// V1.0.39: CreateTaskFromFeedbackModal — align với TaskFormPage chuẩn
// ============================================================

function CreateTaskFromFeedbackModal({ feedback, topics, onClose, onCreated }: {
  feedback: Feedback;
  topics: any[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const { message } = AntdApp.useApp();
  const [form] = Form.useForm();

  // Watch khoa để filter người thực hiện (same logic as TaskFormPage)
  const watchedDepartmentId = Form.useWatch('departmentId', form);

  // Lookup topic + khoa chính
  const topicOfFeedback = topics.find(t => t.id === feedback.topicId);
  const primaryDept = topicOfFeedback?.relatedDepartments?.[0];
  const defaultDeptId = primaryDept?.departmentId ?? undefined;
  const defaultAssigneeId = primaryDept?.managerId ?? undefined;
  const defaultAssigneeName = primaryDept?.managerName ?? undefined;

  // Org tree cho dropdown khoa
  const { data: orgTree = [] } = useQuery({
    queryKey: ['org-tree'],
    queryFn: () => orgService.getTree(),
  });
  const flattenOrg = (nodes: any[], prefix = ''): Array<{label: string; value: string}> => {
    const out: Array<{label: string; value: string}> = [];
    for (const n of nodes) {
      out.push({ label: `${prefix}${n.name}`, value: n.id });
      if (n.children?.length) out.push(...flattenOrg(n.children, prefix + '— '));
    }
    return out;
  };
  const deptOptions = flattenOrg(orgTree);

  // Users — filter theo khoa được chọn (same as TaskFormPage)
  const { data: usersRes } = useQuery({
    queryKey: ['users-list-for-task'],
    queryFn: () => userService.list({ pageSize: 500 }),
  });
  const users = usersRes?.items ?? [];

  const userOptions = users
    .filter((u: any) => {
      if (!watchedDepartmentId) return false;
      const inSecondary = (u.departments ?? []).some((d: any) => d.departmentId === watchedDepartmentId);
      const inPrimary = u.department?.id === watchedDepartmentId;
      return inSecondary || inPrimary;
    })
    .map((u: any) => ({
      value: u.id,
      label: `${u.fullName} — ${u.position ?? ''}`,
    }));

  const createMut = useMutation({
    mutationFn: (values: any) => adminFeedbackV2.createTaskFrom(feedback.id, {
      title: values.title?.trim() || undefined,
      description: values.description?.trim() || undefined,
      departmentId: values.departmentId,
      assigneeId: values.assigneeIds?.[0],
      dueDate: values.dueDate ? dayjs(values.dueDate).format('YYYY-MM-DD') : undefined,
      startDate: values.startDate ? dayjs(values.startDate).format('YYYY-MM-DD') : undefined,
      // V1.0.41: Bỏ priority — BE tự derive từ priorityLevel + importantLevel
      category: values.category,
      watcherIds: values.watcherIds && values.watcherIds.length > 0 ? values.watcherIds : undefined,
      tags: values.tags && values.tags.length > 0 ? values.tags : undefined,
      estimatedHours: values.estimatedHours ? Number(values.estimatedHours) : undefined,
      requiresSignature: !!values.requiresSignature,
      priorityLevel: values.priorityLevel ?? 3,
      importantLevel: values.importantLevel ?? 3,
      kpiRevenuePlan: values.kpiRevenuePlan ?? undefined,
    }),
    onSuccess: (task) => {
      message.success(`Đã tạo task ${task.taskCode}`);
      onCreated();
    },
    onError: (e: any) => message.error(e?.response?.data?.message ?? 'Tạo task thất bại'),
  });

  // Map feedback.priority → important level (HIGH = COMPLAINT)
  // V1.0.41: Bỏ "Mức ưu tiên tổng" — BE auto-derive priority enum từ priorityLevel + importantLevel
  const defaultImportant = feedback.priority === 'HIGH' ? 5 : 3;

  return (
    <Modal
      open
      onCancel={onClose}
      width={820}
      title={
        <div>
          <div style={{ fontFamily: 'serif', fontSize: 16 }}>Tạo task xử lý phản hồi</div>
          <div style={{ fontSize: 11, color: '#7d8a98', marginTop: 4 }}>
            Từ phản hồi {feedback.code} — chủ đề {feedback.topicName ?? '(không xác định)'}
          </div>
        </div>
      }
      onOk={() => form.validateFields().then(v => createMut.mutate(v))}
      okText="Giao task"
      cancelText="Hủy"
      confirmLoading={createMut.isPending}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          title: feedback.topicName ? `${feedback.topicName} — ${feedback.code}` : `Xử lý phản hồi ${feedback.code}`,
          description: `Phản hồi #${feedback.code}:\n\n${feedback.content}`,
          departmentId: defaultDeptId,
          assigneeIds: defaultAssigneeId ? [defaultAssigneeId] : [],
          category: 'OTHER', // Module Hộp thư là task xử lý phản hồi → "Khác"
          priorityLevel: 3,
          importantLevel: defaultImportant,
          dueDate: dayjs().add(7, 'day'),
          startDate: null,
          estimatedHours: null,
          watcherIds: [],
          tags: [],
          requiresSignature: false,
          kpiRevenuePlan: null,
        }}
      >
        <Form.Item label="Tiêu đề" name="title" rules={[{ required: true }]}>
          <Input />
        </Form.Item>

        <Form.Item label="Mô tả chi tiết" name="description">
          <Input.TextArea rows={4} />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Khoa/Phòng thực hiện"
              name="departmentId"
              rules={[{ required: true, message: 'Bắt buộc' }]}
              extra={primaryDept ? `Khoa chính của chủ đề: ${primaryDept.departmentName}` : ''}
            >
              <Select
                showSearch optionFilterProp="label"
                options={deptOptions} placeholder="Chọn khoa..."
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Phân loại" name="category" rules={[{ required: true }]}>
              <Select options={[
                { value: 'ADMIN', label: 'Hành chính' },
                { value: 'TECHNICAL', label: 'Kỹ thuật' },
                { value: 'MEDICAL', label: 'Y tế' },
                { value: 'OTHER', label: 'Khác' },
              ]} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label={<span><span style={{ color: '#f5222d', marginRight: 4 }}>🔴</span> Mức độ Gấp</span>}
              name="priorityLevel"
              tooltip="Càng nhiều sao = càng gấp."
              rules={[{ required: true }]}
            >
              <Rate
                count={5}
                character={({ index = 0, value = 0 }) => (
                  <span style={{
                    fontSize: 24,
                    color: index < value ? '#f5222d' : '#e8e8e8',
                  }}>★</span>
                )}
                tooltips={['Không gấp', 'Ít gấp', 'Bình thường', 'Gấp', 'Rất gấp']}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={<span><span style={{ color: '#fa8c16', marginRight: 4 }}>🟠</span> Mức độ Quan trọng</span>}
              name="importantLevel"
              tooltip="Càng nhiều sao = càng quan trọng."
              rules={[{ required: true }]}
            >
              <Rate
                count={5}
                character={({ index = 0, value = 0 }) => (
                  <span style={{
                    fontSize: 24,
                    color: index < value ? '#fa8c16' : '#e8e8e8',
                  }}>★</span>
                )}
                tooltips={['Không quan trọng', 'Ít quan trọng', 'Bình thường', 'Quan trọng', 'Rất quan trọng']}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item label="Bắt đầu" name="startDate">
              <AntDatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Hạn" name="dueDate" rules={[{ required: true, message: 'Bắt buộc' }]}>
              <AntDatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Thời gian ước tính (giờ)" name="estimatedHours">
              <Input type="number" placeholder="VD: 8" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="💰 KPI Doanh thu kế hoạch (VND)"
              name="kpiRevenuePlan"
              extra="Không bắt buộc."
            >
              <InputNumber
                style={{ width: '100%' }} min={0} step={1000000}
                placeholder="VD: 100,000,000"
                formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={((v: string | undefined) => Number(v?.replace(/\D/g, '') ?? 0)) as any}
                addonAfter="đ"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Người thực hiện"
              name="assigneeIds"
              rules={[{ required: true, message: 'Chọn ít nhất 1 người thực hiện' }]}
              extra={
                !watchedDepartmentId
                  ? 'Chọn khoa/phòng trước'
                  : defaultAssigneeName
                  ? `${userOptions.length} người trong khoa · Mặc định: ${defaultAssigneeName} (trưởng khoa)`
                  : `${userOptions.length} người trong khoa`
              }
            >
              <Select
                mode="multiple"
                showSearch optionFilterProp="label"
                placeholder={watchedDepartmentId ? 'Chọn người...' : 'Chọn khoa trước...'}
                disabled={!watchedDepartmentId}
                options={userOptions}
                notFoundContent={watchedDepartmentId ? 'Không có nhân sự trong khoa này' : null}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Người theo dõi" name="watcherIds">
              <Select
                mode="multiple"
                showSearch optionFilterProp="label"
                placeholder={watchedDepartmentId ? 'Chọn người theo dõi...' : 'Chọn khoa trước...'}
                disabled={!watchedDepartmentId}
                options={userOptions}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Nhãn (tags)" name="tags">
          <Select mode="tags" placeholder="Thêm nhãn..." />
        </Form.Item>

        <Collapse
          ghost
          items={[
            {
              key: 'advanced',
              label: 'Tùy chọn nâng cao',
              children: (
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Form.Item name="requiresSignature" valuePropName="checked" noStyle>
                    <Checkbox>Yêu cầu ký điện tử sau khi hoàn thành</Checkbox>
                  </Form.Item>
                </Space>
              ),
            },
          ]}
        />

        <div style={{ fontSize: 11, color: '#7d8a98', background: '#f0f4f8', padding: 10, borderRadius: 6, marginTop: 8 }}>
          💡 Tag <code>feedback</code> và <code>{feedback.code}</code> sẽ được tự động thêm.
          Sau khi tạo, feedback sẽ tự chuyển sang trạng thái "Đang xử lý".
        </div>
      </Form>
    </Modal>
  );
}

// ============================================================
// V1.0.39: AttachmentItem — preview inline PDF + image, download cho file khác
// ============================================================

function isImageFile(filename: string, mimeType?: string | null): boolean {
  if (mimeType?.startsWith('image/')) return true;
  return /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(filename);
}

function isPdfFile(filename: string, mimeType?: string | null): boolean {
  if (mimeType === 'application/pdf') return true;
  return /\.pdf$/i.test(filename);
}

function formatFileSize(bytes?: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function AttachmentItem({ attachment }: { attachment: { id: string; fileName: string; fileUrl: string; mimeType?: string | null; sizeBytes?: number | null } }) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const isImg = isImageFile(attachment.fileName, attachment.mimeType);
  const isPdf = isPdfFile(attachment.fileName, attachment.mimeType);
  const canPreview = isImg || isPdf;

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Force download bằng cách tạo anchor có `download` attr
    const a = document.createElement('a');
    a.href = attachment.fileUrl;
    a.download = attachment.fileName;
    a.target = '_blank';
    a.click();
  };

  const Icon = isImg ? ImageIcon : (isPdf ? FileText : FileIcon);
  const iconColor = isImg ? '#52c41a' : (isPdf ? '#f5222d' : '#7d8a98');

  return (
    <>
      <div
        onClick={canPreview ? () => setPreviewOpen(true) : handleDownload}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '8px 12px', borderRadius: 6,
          background: '#f8faff',
          cursor: 'pointer',
          border: '1px solid #e8edf2',
          transition: 'all 0.15s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = '#e8f1ff'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = '#f8faff'; }}
      >
        <Icon size={18} style={{ color: iconColor, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {attachment.fileName}
          </div>
          <div style={{ fontSize: 11, color: '#7d8a98' }}>
            {formatFileSize(attachment.sizeBytes)}
            {canPreview && <span style={{ marginLeft: 8, color: '#1e6feb' }}>· Bấm để xem trực tiếp</span>}
            {!canPreview && <span style={{ marginLeft: 8 }}>· Bấm để tải xuống</span>}
          </div>
        </div>
        {canPreview ? (
          <Button type="text" size="small" icon={<Eye size={14} />} onClick={(e) => { e.stopPropagation(); setPreviewOpen(true); }}>
            Xem
          </Button>
        ) : (
          <Button type="text" size="small" icon={<Download size={14} />} onClick={handleDownload}>
            Tải xuống
          </Button>
        )}
      </div>

      {canPreview && previewOpen && (
        <Modal
          open
          onCancel={() => setPreviewOpen(false)}
          width={isPdf ? '90vw' : 'auto'}
          style={{ top: 20, maxWidth: isPdf ? 1200 : '90vw' }}
          footer={
            <Button icon={<Download size={14} />} onClick={handleDownload}>
              Tải xuống
            </Button>
          }
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon size={16} style={{ color: iconColor }} />
              <span style={{ fontSize: 14 }}>{attachment.fileName}</span>
              {attachment.sizeBytes && (
                <span style={{ fontSize: 11, color: '#7d8a98' }}>· {formatFileSize(attachment.sizeBytes)}</span>
              )}
            </div>
          }
          destroyOnClose
        >
          {isImg && (
            <div style={{ textAlign: 'center', maxHeight: '78vh', overflow: 'auto' }}>
              <img
                src={attachment.fileUrl}
                alt={attachment.fileName}
                style={{ maxWidth: '100%', maxHeight: '76vh', objectFit: 'contain' }}
              />
            </div>
          )}
          {isPdf && (
            // V1.0.47: PdfFullViewer cho mobile/iOS render đa trang
            <div style={{ height: '78vh' }}>
              <PdfFullViewer file={attachment.fileUrl} documentName={attachment.fileName} height="100%" />
            </div>
          )}
        </Modal>
      )}
    </>
  );
}

export default FeedbackInboxPage;
